import { useEffect, useState } from 'react';
import type { LedgerAggregate, LedgerTransferOutcome, ScoredRecommendation } from '@fantasy/types';
import { api, ApiRequestError } from '../api.ts';

/** Below this many scored gameweeks, a rate reads like a track record it
 * isn't — "2 for 2" from a fortnight is coin-flip noise dressed up as a
 * pattern. The raw counts stay honest at any sample size, but the rate
 * itself is withheld until there's enough of it to actually mean something.
 * See `LedgerAggregate`'s own doc comment for the same reasoning applied to
 * "never expose rate without total". */
const MIN_GAMEWEEKS_FOR_RATE = 3;

function formatRate(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

interface AggregateSummaryProps {
  aggregate: LedgerAggregate;
}

/** Counts first, rate second, and never the rate alone — see
 * `LedgerAggregate`'s doc comment. Below `MIN_GAMEWEEKS_FOR_RATE`, the rate
 * is withheld entirely rather than shown small-sample-flattering. */
function AggregateSummary({ aggregate }: AggregateSummaryProps) {
  const tooFewGameweeks = aggregate.gameweeksScored < MIN_GAMEWEEKS_FOR_RATE;

  return (
    <div className="ledger-aggregate">
      <p className="hint">
        Scored across {aggregate.gameweeksScored} gameweek{aggregate.gameweeksScored === 1 ? '' : 's'}.
      </p>

      {tooFewGameweeks ? (
        <p>
          Too few gameweeks scored yet for a rate to mean anything — check back once a few more have
          played out.
        </p>
      ) : (
        <dl className="ledger-stats">
          <div>
            <dt>Captain calls</dt>
            <dd>
              {aggregate.captain.beatOwnCount} of {aggregate.captain.total} beat your own pick (
              {formatRate(aggregate.captain.rate)})
            </dd>
          </div>
          <div>
            <dt>Transfers</dt>
            <dd>
              {aggregate.transfers.total === 0
                ? 'No transfers recommended yet.'
                : `${aggregate.transfers.gainedCount} of ${aggregate.transfers.total} gained points over the player dropped (${formatRate(aggregate.transfers.rate)})`}
            </dd>
          </div>
        </dl>
      )}
    </div>
  );
}

interface OutcomeRowProps {
  label: string;
  detail: string;
  won: boolean;
}

/** One scored call — captain or transfer — rendered identically whether it
 * won or lost. `data-outcome` carries colour as pure decoration on top of
 * the "Hit"/"Miss" text already in the DOM, the same convention
 * `LeagueStandings`' `MovementCell` uses for rank movement: nothing here
 * relies on colour alone, and a miss gets the same size, weight and
 * position as a hit — no collapsed section, no smaller type. */
function OutcomeRow({ label, detail, won }: OutcomeRowProps) {
  return (
    <div className="ledger-outcome" data-outcome={won ? 'hit' : 'miss'}>
      <span className="ledger-outcome-label">{label}</span>
      <span className="ledger-outcome-detail">{detail}</span>
      <span className="ledger-outcome-tag">{won ? 'Hit' : 'Miss'}</span>
    </div>
  );
}

interface RecommendationCardProps {
  recommendation: ScoredRecommendation;
}

function RecommendationCard({ recommendation }: RecommendationCardProps) {
  const { captain, transfers, outcome } = recommendation;

  return (
    <div className="ledger-recommendation">
      <p className="hint hint--inline">{new Date(recommendation.generatedAt).toLocaleDateString()}</p>

      <OutcomeRow
        label="Captain"
        detail={`Recommended ${captain.pick.name} (${outcome.captain.recommendedPoints} pts) over your ${captain.own.name} (${outcome.captain.ownPoints} pts)`}
        won={outcome.captain.beatOwn}
      />

      {transfers.length === 0 ? (
        <p className="hint">No transfer recommended — hold.</p>
      ) : (
        transfers.map((transfer, i) => {
          const transferOutcome: LedgerTransferOutcome | undefined = outcome.transfers[i];
          if (!transferOutcome) return null;
          return (
            <OutcomeRow
              key={`${transfer.out.name}-${transfer.in.name}`}
              label="Transfer"
              detail={`${transfer.out.name} (${transferOutcome.outPoints} pts) → ${transfer.in.name} (${transferOutcome.inPoints} pts)`}
              won={transferOutcome.gained}
            />
          );
        })
      )}
    </div>
  );
}

/** Groups already-sorted (most-recent-first, per `listScored`) recommendations
 * by gameweek, preserving that order — a `Map` rather than a plain object so
 * insertion order is guaranteed regardless of the numeric gameweek keys. */
function groupByGameweek(recommendations: ScoredRecommendation[]): Map<number, ScoredRecommendation[]> {
  const groups = new Map<number, ScoredRecommendation[]>();
  for (const recommendation of recommendations) {
    const existing = groups.get(recommendation.gameweek);
    if (existing) {
      existing.push(recommendation);
    } else {
      groups.set(recommendation.gameweek, [recommendation]);
    }
  }
  return groups;
}

/**
 * The advice record (T-058) — the honesty ledger's own screen. Depends on
 * T-057's `/api/ledger` for both the aggregate and every individual scored
 * recommendation; see `packages/types/src/ledger.ts` for why nothing here
 * is allowed to summarise itself into a single flattering number. The tone
 * matters more than the layout: a miss reads exactly as prominently as a
 * hit, with no "we were right" framing anywhere on the page — it simply
 * reports what was advised and what happened.
 */
export function AdviceLedger() {
  const [recommendations, setRecommendations] = useState<ScoredRecommendation[] | null>(null);
  const [aggregate, setAggregate] = useState<LedgerAggregate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getLedger()
      .then(({ recommendations: fetched, aggregate: fetchedAggregate }) => {
        if (cancelled) return;
        setRecommendations(fetched);
        setAggregate(fetchedAggregate);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiRequestError ? err.message : 'Could not load your advice record.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="panel panel--accent-2" aria-labelledby="ledger-heading">
      <div className="panel-head">
        <div>
          <h3 id="ledger-heading">Advice Record</h3>
          <p className="hint">Every recommendation, scored once its gameweek is played.</p>
        </div>
      </div>

      {loading && (
        <p className="hint" aria-live="polite">
          Loading your advice record…
        </p>
      )}

      {!loading && error && (
        <ul className="notices notices--flush">
          <li className="error">{error}</li>
        </ul>
      )}

      {!loading && !error && recommendations && recommendations.length === 0 && (
        <p className="hint">
          Nothing scored yet. Every recommendation you're given gets recorded here, and once its
          gameweek is played this will show what was advised against what it actually returned —
          captain calls and transfers alike, wins and losses both.
        </p>
      )}

      {!loading && !error && recommendations && recommendations.length > 0 && aggregate && (
        <>
          <AggregateSummary aggregate={aggregate} />

          {Array.from(groupByGameweek(recommendations).entries()).map(([gameweek, recs]) => (
            <section key={gameweek} className="ledger-gameweek" aria-labelledby={`ledger-gw-${gameweek}-heading`}>
              <h4 id={`ledger-gw-${gameweek}-heading`}>Gameweek {gameweek}</h4>
              {recs.map((recommendation) => (
                <RecommendationCard key={recommendation.id} recommendation={recommendation} />
              ))}
            </section>
          ))}
        </>
      )}
    </section>
  );
}
